import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { cn } from "@omni/utils/styles";
import { Dispatch, SetStateAction, useEffect } from "react";

import CalculationField from "@/components/Calculator/Components/CalculationField/calculationField.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { Dropdown } from "@/components/General/Dropdown/dropdown.component";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { DamImage } from "@/utils/dam.utils";

import { CalculatorImage } from "../../Components/CalculatorImage/calculatorImage.component";
import { RoofShape, RoofShapeProperties } from "../epdmCalculator";

type Props = {
    setRoofShapeProperties: Dispatch<SetStateAction<RoofShapeProperties>>;
    setRoofShape: Dispatch<SetStateAction<RoofShape>>;
    setIsolationProduct: Dispatch<
        SetStateAction<undefined | Product<ProductFieldSet.ProductTile>>
    >;
    roofShapeProperties: RoofShapeProperties;
    roofShape: RoofShape;
    isolationProduct?: Product<ProductFieldSet.ProductTile>;
    isolationProducts: Array<Product<ProductFieldSet.ProductTile>>;
    isolationRequired: boolean;
    setIsolationRequired: Dispatch<SetStateAction<boolean>>;
};

const Calculation = ({
    setRoofShapeProperties,
    roofShapeProperties,
    roofShape,
    setRoofShape,
    isolationProducts,
    isolationProduct,
    setIsolationProduct,
    isolationRequired,
    setIsolationRequired
}: Props) => {
    const { t } = useGranite();

    const handleCalculationValue = (key: string, value: string) => {
        setRoofShapeProperties((state) => ({
            ...state,
            [key]: value
        }));
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (
            isolationRequired &&
            !isolationProduct &&
            isolationProducts.length > 0
        ) {
            setIsolationProduct(isolationProducts.at(0));
        }
    }, [isolationRequired, isolationProduct, isolationProducts]);

    const label =
        roofShape === RoofShape.RECTANGLE
            ? t("calculator.epdmCalculator.roofShape.rectangle")
            : t("calculator.epdmCalculator.roofShape.lShape");

    return (
        <div className={cn("tw:mt-6 calculator")}>
            <Row>
                <Col
                    className={cn("tw:mt-6")}
                    md={6}
                >
                    <h3 className={cn("tw:sm:block")}>
                        {t("calculator.epdmCalculator.roofShape")}
                    </h3>
                    <Dropdown
                        placement={"bottom"}
                        isLabel={true}
                    >
                        <Dropdown.Toggle>
                            <Dropdown.Label>{label}</Dropdown.Label>
                            <Dropdown.Caret />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() =>
                                    setRoofShape(RoofShape.RECTANGLE)
                                }
                                label={t(
                                    "calculator.epdmCalculator.roofShape.rectangle"
                                )}
                            />
                            <Dropdown.Item
                                onClick={() => setRoofShape(RoofShape.L_SHAPED)}
                                label={t(
                                    "calculator.epdmCalculator.roofShape.lShape"
                                )}
                            />
                        </Dropdown.Menu>
                    </Dropdown>
                    <h3 className="d-sm-block mt-4 mb-2">
                        {t("calculator.epdmCalculator.requiredMaterial")}
                    </h3>
                    <FormGroup toggle={true}>
                        <Label>
                            <Input
                                type="checkbox"
                                checked={isolationRequired}
                                onChange={(e) => {
                                    setIsolationRequired(
                                        (e.target as HTMLInputElement).checked
                                    );
                                }}
                            />{" "}
                            {t("calculator.epdmCalculator.isolation")}
                        </Label>
                    </FormGroup>
                    {isolationRequired && isolationProducts.length > 0 && (
                        <Dropdown
                            className={cn("tw:mt-1")}
                            placement={"bottom"}
                            isLabel={true}
                        >
                            <Dropdown.Toggle>
                                <Dropdown.Label>
                                    {isolationProduct?.title ||
                                        isolationProducts.at(0)?.title}
                                    <Dropdown.Caret />
                                </Dropdown.Label>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {isolationProducts.map((p) => (
                                    <Dropdown.Item
                                        key={p.sku}
                                        onClick={() => setIsolationProduct(p)}
                                        label={p.title}
                                    />
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </Col>
                <Col
                    className={cn("tw:mt-6")}
                    md={6}
                >
                    {roofShape === RoofShape.L_SHAPED && (
                        <CalculatorImage
                            className={cn("tw:mt-4")}
                            size={"large"}
                            src={DamImage.calculator.epdmLShape}
                            alt={"calculator-aquaplan-L-vormig-dak-epdm.png"}
                        />
                    )}
                    {roofShape === RoofShape.RECTANGLE && (
                        <CalculatorImage
                            className={cn("tw:mt-4")}
                            size={"large"}
                            src={DamImage.calculator.epdmRectangleShape}
                            alt={"calculator-aquaplan-rechthoekig-dak-epdm.png"}
                        />
                    )}
                </Col>
            </Row>
            <h3 className={cn("tw:sm:block tw:mt-6 tw:mb-0")}>
                {roofShape === RoofShape.RECTANGLE
                    ? t("calculator.epdmCalculator.measurementsRectangularRoof")
                    : t(
                          "calculator.epdmCalculator.measurementsLShapedRoofPart1"
                      )}
            </h3>
            <Row>
                <CalculationField
                    name="widthA1"
                    dimension="widthA1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.widthA1 && {
                        value: roofShapeProperties.widthA1
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="heightB1"
                    dimension="heightB1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.heightB1 && {
                        value: roofShapeProperties.heightB1
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="lengthC1"
                    dimension="lengthC1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.lengthC1 && {
                        value: roofShapeProperties.lengthC1
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="widthW1"
                    dimension="widthW1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.widthW1 && {
                        value: roofShapeProperties.widthW1
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
            </Row>
            {roofShape === RoofShape.L_SHAPED && (
                <>
                    <h3 className={cn("tw:sm:block tw:mt-6 tw:mb-0")}>
                        {t(
                            "calculator.epdmCalculator.measurementsLShapedRoofPart2"
                        )}
                    </h3>
                    <Row>
                        <CalculationField
                            name="lengthC2"
                            dimension="lengthC2"
                            unit="cm"
                            setCalculationValue={handleCalculationValue}
                            {...(roofShapeProperties.lengthC2 && {
                                value: roofShapeProperties.lengthC2
                            })}
                            sm={6}
                            md={3}
                            lg={3}
                        />
                        <CalculationField
                            name="widthW2"
                            dimension="widthW2"
                            unit="cm"
                            setCalculationValue={handleCalculationValue}
                            {...(roofShapeProperties.widthW2 && {
                                value: roofShapeProperties.widthW2
                            })}
                            sm={6}
                            md={3}
                            lg={3}
                        />
                    </Row>
                </>
            )}
        </div>
    );
};

export default Calculation;
